import { Alert, AlertTitle, Box, Button } from '@mui/material';

import { FileDuplicateSummary } from '../../../types/syntiro_honey';
import { BodyText } from '../../shared/typography/PrimaryTypography';

interface FileDuplicateBannerProps {
  /** The scoped file's display name. */
  fileName: string;
  /** The file's unresolved collision counts. */
  summary: FileDuplicateSummary;
  /** Open the 4-way resolution dialog. */
  onOpenDialog: () => void;
  /** Filter the list to the collision-flagged records. */
  onViewAffected: () => void;
}

/**
 * The file-level duplicate warning at the top of a file-scoped review (Maxine
 * 2026-08-19 scenario 2): names how many records already exist on Syntiro, split
 * into same-value duplicates and differing-value conflicts, and offers the one
 * decision that settles all of them. Rendered only while the decision is
 * unanswered — a resolved (or acknowledged-at-upload) file never re-asks.
 * @param {string} fileName The scoped file's display name.
 * @param {FileDuplicateSummary} summary Unresolved collision counts.
 * @param {() => void} onOpenDialog Opens the resolution dialog.
 * @param {() => void} onViewAffected Applies the collision-flag filter.
 * @returns {JSX.Element}
 */
export default function FileDuplicateBanner({
  fileName,
  summary,
  onOpenDialog,
  onViewAffected
}: FileDuplicateBannerProps) {
  const total = summary.duplicateCount + summary.conflictCount;
  return (
    <Alert
      severity="warning"
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button color="inherit" size="small" onClick={onViewAffected}>
            View affected records
          </Button>
          <Button color="warning" variant="contained" size="small" onClick={onOpenDialog}>
            Choose how to handle…
          </Button>
        </Box>
      }
    >
      <AlertTitle>{`${total} record${total === 1 ? '' : 's'} in this file already exist on Syntiro`}</AlertTitle>
      <BodyText>
        {`In "${fileName}": ${summary.duplicateCount} with the same value, ${summary.conflictCount} with a different value. Decide once how to handle them — update the earlier values, ignore them, keep everything, or delete the file.`}
      </BodyText>
    </Alert>
  );
}
